import { AnimatePresence, motion } from "motion/react";
import { bool, number, oneOfType, string } from "prop-types";
import { useEffect, useState } from "react";

import styled from "styled-components";

import FnrOpacityToggle from "UI/FnrOpacityToggle";
import FnrTypography from "UI/FnrTypography";
import { colors, colorsByFunction } from "UI/colors";
import { inOutZoom } from "UI/motionStyles";

import { addOpacity } from "Utilities/Colors/colorUtils";
import dynRight from "Utilities/interpolations/dynRight";
import dynTop from "Utilities/interpolations/dynTop";

const FnrNumericIndicator = ({
  count,
  show,
  top = 0,
  right = 0,
  inline,
  scale = 0.65,
  color,
  backgroundColor = colors.white,
  uncapped,
}) => {
  const [counts, setCounts] = useState([count]);
  const isCapped = !uncapped && count > 99;
  // Past 99 an uncapped count outgrows the circle. The animated labels are
  // absolute so they can cross-fade, which means they can't size the pill;
  // an invisible copy in the flow does it instead
  const isPill = uncapped && count > 99;

  useEffect(() => {
    if (!counts.includes(count)) {
      setCounts(Array.from(new Set([...counts, count])).sort((a, b) => a - b));
    }
  }, [count, counts]);

  return (
    <Wrapper
      data-cy="FnrNumericIndicator"
      show={Boolean(count) && show}
      scale
      $top={top}
      $right={right}
      $inline={inline}
      $scale={scale}
      $backgroundColor={backgroundColor}
      $isPill={isPill}
    >
      {isPill && (
        <PillSizer aria-hidden>
          <FnrTypography bold type="number" fontSize="sm">
            {count}
          </FnrTypography>
        </PillSizer>
      )}
      <AnimatePresence initial={false}>
        {counts.map((localCount) => {
          if (Boolean(count) && localCount === count)
            return (
              <SMotionDiv {...inOutZoom} key={count} $isPill={isPill}>
                <FnrTypography
                  bold
                  color={color}
                  type="number"
                  fontSize={isCapped ? "xs" : "sm"}
                >
                  {isCapped ? 99 : count}
                  {isCapped && <SSup>+</SSup>}
                </FnrTypography>
              </SMotionDiv>
            );
          return null;
        })}
      </AnimatePresence>
    </Wrapper>
  );
};

const SSup = styled.sup`
  vertical-align: text-top;
`;

const PillSizer = styled.span`
  visibility: hidden;
  padding-inline: 6px;
`;

const SMotionDiv = styled(motion.div)`
  position: absolute;
  width: ${({ $isPill }) => ($isPill ? "100%" : "24px")};
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled(FnrOpacityToggle)`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ $inline }) => ($inline ? "" : "position: absolute;")}
  ${dynTop}
  ${dynRight}
  transform: ${({ $inline }) =>
    $inline ? "" : "translate(25%, -25%)"} scale(${({ $scale }) => $scale});
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 12px;
  width: ${({ $isPill }) => ($isPill ? "auto" : "24px")};
  min-width: 24px;
  height: 24px;
  border: 1px solid ${addOpacity(colorsByFunction.textLight, 0.8)};
  overflow: hidden;
`;

FnrNumericIndicator.propTypes = {
  count: number,
  show: bool,
  top: oneOfType([string, number]),
  right: oneOfType([string, number]),
  inline: bool,
  scale: number,
  color: string,
  backgroundColor: string,
  // NavBar badges cap at 99+; a count someone reads for its value shouldn't
  uncapped: bool,
};

export default FnrNumericIndicator;
